import { createContext, useMemo } from 'react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { AppInsightsErrorBoundary, ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';

// Application Insights error tracking etc. is used only if the connection string is defined in runtimeConfig.js,
// which it normally would be only when deployed to Azure.  When running locally, without the connection string,
// most of the code is skipped and context consumers must check that appInsights is actually defined on it.
let reactPlugin: ReactPlugin | undefined = undefined;
let appInsights: ApplicationInsights | undefined = undefined;
const appInsightsConnString = window.runtimeConfig.APP_INSIGHTS_CONNECTION_STRING;
if (appInsightsConnString !== '#{appInsightsConnectionString}#') {
  reactPlugin = new ReactPlugin();
  const clickPlugin = new ClickAnalyticsPlugin();
  appInsights = new ApplicationInsights({
    config: {
      connectionString: appInsightsConnString,
      enableAutoRouteTracking: true,
      extensions: [reactPlugin, clickPlugin],
      extensionConfig: {
        [clickPlugin.identifier]: {
          autoCapture: true
        }
      },
    }
  });
  appInsights.loadAppInsights();
}

// we define our own context provider so that ApplicationInsights object is also available to components,
// not only ReactPlugin (from which you cannot access e. g. setAuthenticatedUserContext())
export type AppInsightsContextType = {
  appInsights?: ApplicationInsights;
  reactPlugin?: ReactPlugin;
};

interface IProps {
  children: JSX.Element;
}

export const AppInsightsContext = createContext<AppInsightsContextType>({
  appInsights: undefined,
  reactPlugin: undefined
});

export const AppInsightsContextProvider = ({ children }: IProps): JSX.Element => {
  const contextValue = useMemo<AppInsightsContextType>(() => ({ appInsights, reactPlugin }), [appInsights, reactPlugin]);

  // noop if nothing is defined
  if (!appInsights) {
    return children;
  }
  
  // not sure if it is always safe to use a localized string in a global error handler...
  return (
    <AppInsightsErrorBoundary
      onError={() => <div className="alert alert-danger">Something went wrong.  Please refresh the page</div>}
      appInsights={reactPlugin!}>
      <AppInsightsContext.Provider value={contextValue}>
        {children}
      </AppInsightsContext.Provider>
    </AppInsightsErrorBoundary>
  );
};